import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store'
import { Message } from 'element-ui'

Vue.use(Router)

// 解决重复点击路由报错
const originalPush = Router.prototype.push
Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => err)
}

// 静态路由
export const constantRoutes = [
  {
    path: '/login',
    component: () => import('@/views/login/index'),
    hidden: true
  },
  {
    path: '/404',
    component: () => import('@/views/error/404'),
    hidden: true
  },
  {
    path: '/',
    redirect: '/dashboard'
  },
  {
    path: '/debug',
    component: () => import('@/views/debug'),
    hidden: true
  },
  {
    path: '/dashboard',
    component: () => import('@/components/layout/index'),
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: () => import('@/views/dashboard/index'),
        meta: { title: '仪表板', icon: 'el-icon-s-home' }
      }
    ]
  }
]

// 动态路由
export const asyncRoutes = [
  {
    path: '/admin',
    component: () => import('@/components/layout/index'),
    children: [
      {
        path: '',
        name: 'AdminList',
        component: () => import('@/views/admin/list'),
        meta: { 
          title: '管理员管理', 
          icon: 'el-icon-user-solid',
          permissions: ['SYSTEM_MANAGE'] 
        }
      }
    ]
  },
  {
    path: '/user',
    component: () => import('@/components/layout/index'),
    meta: {
      title: '用户管理',
      icon: 'el-icon-user',
      permissions: ['USER_MANAGE']
    },
    children: [
      {
        path: 'list',
        name: 'UserList',
        component: () => import('@/views/user/list'),
        meta: { title: '用户列表', permissions: ['USER_MANAGE'] }
      }
    ]
  },
  {
    path: '/group',
    component: () => import('@/components/layout/index'),
    meta: {
      title: '群组管理',
      icon: 'el-icon-s-custom',
      permissions: ['GROUP_MANAGE']
    },
    children: [
      {
        path: 'list',
        name: 'GroupList',
        component: () => import('@/views/group/list'),
        meta: { title: '群组列表', permissions: ['GROUP_MANAGE'] }
      }
    ]
  },
  // 404页面必须放在最后
  { path: '*', redirect: '/404', hidden: true }
]

const createRouter = () => new Router({
  mode: 'history',
  base: process.env.NODE_ENV === 'production' ? '/' : '/',
  scrollBehavior: () => ({ y: 0 }),
  routes: constantRoutes
})

const router = createRouter()

// 重置路由
export function resetRouter() {
  const newRouter = createRouter()
  router.matcher = newRouter.matcher
}

// 路由守卫
const whiteList = ['/login'] // 白名单

router.beforeEach(async(to, from, next) => {
  // 设置页面标题
  if (to.meta && to.meta.title) {
    document.title = `${to.meta.title} - 小白资讯管理员系统`
  } else {
    document.title = '小白资讯管理员系统'
  }

  // 获取token
  const hasToken = store.getters.token

  if (hasToken) {
    // 检查token是否过期（这里可以添加更复杂的token验证逻辑）
    // 如果token格式不正确或明显过期，直接清除
    if (hasToken.length < 10) {
      await store.dispatch('admin/logout')
      next(`/login?redirect=${to.path}`)
      return
    }
    if (to.path === '/login') {
      // 如果已经登录，重定向到首页
      next({ path: '/' })
    } else {
      // 检查是否已经获取用户信息
      const hasGetUserInfo = store.getters.adminInfo && store.getters.adminInfo.id
      const hasAddRouters = store.getters.addRouters && store.getters.addRouters.length > 0
      
      // 如果用户信息已获取但路由未生成，需要生成路由
      if (hasGetUserInfo && !hasAddRouters) {
        try {
          // 根据权限生成动态路由
          const accessRoutes = await generateRoutes(store.getters.permissions)
          console.log('Generated routes on demand:', accessRoutes)
          
          // 动态添加路由
          router.addRoutes(accessRoutes)
          
          // 保存路由到store中，供侧边栏使用
          store.dispatch('app/setRouters', accessRoutes)
          console.log('Routes saved to store on demand:', accessRoutes)
          
          // 确保addRoutes完成后再跳转
          next({ ...to, replace: true })
        } catch (error) {
          console.error('生成路由失败:', error)
          next()
        }
      } else if (hasGetUserInfo && hasAddRouters) {
        // 用户信息已获取且路由已生成，直接通过
        next()
      } else {
        try {
          // 如果用户信息未获取，先获取用户信息
          if (!hasGetUserInfo) {
            await store.dispatch('admin/getInfo')
          }
          
          // 根据权限生成动态路由
          const accessRoutes = await generateRoutes(store.getters.permissions)
          console.log('Generated routes:', accessRoutes)
          
          // 动态添加路由
          router.addRoutes(accessRoutes)
          
          // 保存路由到store中，供侧边栏使用
          store.dispatch('app/setRouters', accessRoutes)
          console.log('Routes saved to store:', accessRoutes)
          
          // 确保addRoutes完成后再跳转
          next({ ...to, replace: true })
        } catch (error) {
          // 移除token并跳转到登录页
          await store.dispatch('admin/resetToken')
          Message.error(error || '验证失败，请重新登录')
          next(`/login?redirect=${to.path}`)
        }
      }
    }
  } else {
    // 没有token
    if (whiteList.indexOf(to.path) !== -1) {
      // 在免登录白名单，直接进入
      next()
    } else {
      // 其他没有访问权限的页面将被重定向到登录页面
      next(`/login?redirect=${to.path}`)
    }
  }
})

// 根据权限生成路由
function generateRoutes(permissions) {
  return new Promise(resolve => {
    let accessedRoutes
    console.log('generateRoutes called with permissions:', permissions)
    console.log('asyncRoutes:', asyncRoutes)
    
    // 如果没有权限信息，给默认权限
    if (!permissions || permissions.length === 0) {
      permissions = ['SYSTEM_MANAGE', 'USER_MANAGE', 'GROUP_MANAGE']
    }
    
    if (permissions.includes('*') || permissions.includes('SYSTEM_MANAGE')) {
      // 超级管理员或系统管理员拥有所有权限
      accessedRoutes = asyncRoutes
      console.log('Using all async routes for super admin')
    } else {
      // 根据权限过滤路由
      accessedRoutes = filterAsyncRoutes(asyncRoutes, permissions)
      console.log('Filtered routes:', accessedRoutes)
    }
    console.log('Final accessedRoutes:', accessedRoutes)
    resolve(accessedRoutes)
  })
}

// 过滤异步路由
function filterAsyncRoutes(routes, permissions) {
  const res = []

  routes.forEach(route => {
    const tmp = { ...route }
    if (hasPermission(permissions, tmp)) {
      if (tmp.children) {
        tmp.children = filterAsyncRoutes(tmp.children, permissions)
      }
      res.push(tmp)
    }
  })

  return res
}

// 判断是否有权限
function hasPermission(permissions, route) {
  if (route.meta && route.meta.permissions) {
    return permissions.some(permission => route.meta.permissions.includes(permission))
  } else {
    return true
  }
}

export default router
